import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/catalog?search=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <>
      <header className="fixed top-0 w-full z-50 bg-black/60 backdrop-blur-xl border-b border-white/5">
        {/* B2B Overline strip */}
        <div className="w-full bg-white text-black py-1.5 flex justify-center items-center text-[10px] sm:text-xs font-semibold tracking-[0.25em] uppercase" data-testid="b2b-overline">
          <span>Wholesale · B2B · Distribuidor Autorizado</span>
        </div>
        <div className="px-4 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="header-logo" className="text-2xl font-serif tracking-tighter">
              <span className="font-['Playfair_Display'] font-bold">HAUTE</span>
            </Link>

            <nav className="hidden lg:flex items-center gap-12" data-testid="desktop-nav">
              <Link to="/" className="text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white transition-colors duration-300">Inicio</Link>
              <Link to="/catalog" className="text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white transition-colors duration-300">Catálogo</Link>
              <Link to="/brands" className="text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white transition-colors duration-300">Marcas</Link>
              <Link to="/contact" className="text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white transition-colors duration-300">Contacto</Link>
            </nav>

            <div className="flex items-center gap-3 md:gap-5">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                data-testid="search-button"
                className="p-3 border border-white/10 hover:border-white/40 rounded-full transition-all duration-300"
              >
                <Search className="w-4 h-4" />
              </button>

              <Link
                to="/portal/login"
                data-testid="header-portal-login"
                className="hidden md:block text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white transition-colors duration-300"
              >
                Login B2B
              </Link>
              <Link
                to="/portal/register"
                data-testid="header-portal-register"
                className="hidden sm:inline-flex items-center bg-white text-black px-4 md:px-5 py-2.5 text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold hover:bg-gray-200 transition-colors"
              >
                Registro B2B
              </Link>

              {user ? (
                <Link
                  to="/admin"
                  data-testid="admin-link"
                  className="p-3 border border-white/10 hover:border-white/40 rounded-full transition-all duration-300"
                >
                  <User className="w-4 h-4" />
                </Link>
              ) : (
                <Link
                  to="/admin/login"
                  data-testid="login-link"
                  className="hidden lg:block text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white/80 transition-colors duration-300"
                >
                  Admin
                </Link>
              )}

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
                className="lg:hidden p-3 border border-white/10 hover:border-white/40 rounded-full transition-all duration-300"
              >
                {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {searchOpen && (
          <div className="bg-[#0A0A0A] border-t border-white/5 px-4 md:px-12 lg:px-24 py-6" data-testid="search-panel">
            <form onSubmit={handleSearch}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar productos, marcas..."
                data-testid="search-input"
                className="w-full bg-transparent border-b border-white/20 focus:border-white outline-none px-0 py-4 text-white placeholder-gray-600 rounded-none transition-colors duration-300 font-light"
                autoFocus
              />
            </form>
          </div>
        )}

        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#0A0A0A] border-t border-white/5" data-testid="mobile-menu">
            <nav className="flex flex-col px-4 py-6 gap-4">
              <Link to="/" className="text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white transition-colors duration-300 py-3">Inicio</Link>
              <Link to="/catalog" className="text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white transition-colors duration-300 py-3">Catálogo</Link>
              <Link to="/brands" className="text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white transition-colors duration-300 py-3">Marcas</Link>
              <Link to="/contact" className="text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white transition-colors duration-300 py-3">Contacto</Link>
              <div className="h-px bg-white/10 my-2" />
              <Link to="/portal/login" className="text-xs uppercase tracking-[0.2em] text-white hover:text-white transition-colors duration-300 py-3">Login B2B</Link>
              <Link to="/portal/register" className="bg-white text-black px-4 py-3 text-xs uppercase tracking-[0.2em] font-semibold text-center">Registro B2B</Link>
              {user ? (
                <>
                  <Link to="/admin" className="text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors duration-300 py-3">Admin Panel</Link>
                  <button onClick={logout} className="text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors duration-300 py-3 text-left">Cerrar Sesión</button>
                </>
              ) : (
                <Link to="/admin/login" className="text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors duration-300 py-3">Admin Login</Link>
              )}
            </nav>
          </div>
        )}
      </header>
      <div className="h-[110px]" />
    </>
  );
};

export default Header;
